import { tr } from '@/lib/i18n-core';
import { Plus, Trash2 } from 'lucide-react';
import { humanize } from '@/lib/utils';
import { Button, Input, Select } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';

export interface Sensitivity {
  antibiotic: string;
  result: 'sensitive' | 'intermediate' | 'resistant';
  mic?: string;
}

export interface SheetLatestResult {
  id: string;
  value: string;
  flag: string;
  unit: string | null;
  comment: string | null;
  culture: { organism: string; colonyCount?: string; sensitivities: Sensitivity[] } | null;
  enteredAt: string;
  verifiedAt: string | null;
  enteredBy: string | null;
  version: number;
  isAmended: boolean;
  previousValue: string | null;
  amendReason: string | null;
  isCritical: boolean;
  criticalAckAt: string | null;
  criticalAckTo: string | null;
  source?: string;
  analyserValue?: string | null;
  overrideReason?: string | null;
  overridePending?: boolean;
  restored?: boolean;
}

export interface SheetItem {
  id: string;
  status: string;
  accessionNo: string | null;
  panel: { id: string; code: string; name: string } | null;
  test: {
    id: string;
    code: string;
    name: string;
    departmentGroup: string;
    unit: string | null;
    resultType: string;
    selectOptions: string[];
    decimals: number | null;
    quickValues: string[];
    defaultValue: string | null;
    criticalLow: number | null;
    criticalHigh: number | null;
    method: string | null;
  };
  reference: { low: number | null; high: number | null; text: string; widest?: boolean } | null;
  resultHidden?: boolean;
  latestResult: SheetLatestResult | null;
  previous: { value: string; numericValue: number | null; enteredAt: string } | null;
}

export interface QcGate {
  closed: { testId: string; testName: string; message: string }[];
  canOverride: boolean;
}

export interface Sheet {
  qcGate?: QcGate;
  separateVerifier?: boolean;
  analyserOverride?: AnalyserOverrideMode;
  order: { id: string; number: string; priority: string; orderedAt: string; clinicalNotes: string | null };
  patient: { id: string; mrn: string; fullName: string; sex: string; dob: string | null };
  items: SheetItem[];
}

export type AnalyserOverrideMode = 'technician' | 'with_reason' | 'with_approval' | 'verifier_only';

export interface Draft {
  value: string;
  comment: string;
  overrideReason: string;
  flag: string;
  organism: string;
  colonyCount: string;
  sensitivities: Sensitivity[];
}

export const LOCKED = new Set(['verified', 'released', 'cancelled']);
export const NOT_YET = new Set(['ordered']);

export function seedDraft(item: SheetItem): Draft {
  const closedToEntry = NOT_YET.has(item.status) || LOCKED.has(item.status);
  return {
    value: item.latestResult?.value ?? (closedToEntry ? '' : (item.test.defaultValue ?? '')),
    comment: '',
    overrideReason: '',
    flag: '',
    organism: item.latestResult?.culture?.organism ?? '',
    colonyCount: item.latestResult?.culture?.colonyCount ?? '',
    sensitivities: item.latestResult?.culture?.sensitivities ?? [],
  };
}

export function buildEntry(item: SheetItem, draft: Draft) {
  const culture = item.test.resultType === 'culture';
  return {
    orderItemId: item.id,
    value: culture ? draft.organism.trim() : draft.value.trim(),
    comment: draft.comment.trim() || undefined,
    ...(draft.overrideReason.trim() ? { overrideReason: draft.overrideReason.trim() } : {}),
    ...(!culture && item.test.resultType === 'numeric' && !item.reference && draft.flag ? { flag: draft.flag } : {}),
    ...(culture
      ? {
          culture: {
            organism: draft.organism.trim(),
            colonyCount: draft.colonyCount.trim() || undefined,
            sensitivities: draft.sensitivities.filter((s) => s.antibiotic.trim()),
          },
        }
      : {}),
  };
}

export function parseNumeric(value: string): number | null {
  const match = value.trim().match(/^[<>]?\s*(-?\d+(?:\.\d+)?)/);
  return match ? Number(match[1]) : null;
}

export function flagFor(item: SheetItem, raw: string): string {
  if (item.test.resultType !== 'numeric') return 'normal';
  const value = parseNumeric(raw);
  if (value === null) return 'normal';
  if (item.test.criticalLow !== null && value <= item.test.criticalLow) return 'critical_low';
  if (item.test.criticalHigh !== null && value >= item.test.criticalHigh) return 'critical_high';
  if (!item.reference || (item.reference.low === null && item.reference.high === null)) return 'no_range';
  if (item.reference.low !== null && value < item.reference.low) return 'low';
  if (item.reference.high !== null && value > item.reference.high) return 'high';
  return 'normal';
}

export function ResultInput({
  item,
  draft,
  disabled,
  bare,
  onChange,
  onKeyDown,
}: {
  item: SheetItem;
  draft: Draft;
  disabled: boolean;
  bare?: boolean;
  onChange: (patch: Partial<Draft>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
}) {
  const entryProps = bare ? {} : { 'data-entry-item': item.id };
  switch (item.test.resultType) {
    case 'select':
      return (
        <Select
          {...entryProps}
          className="h-8 min-w-[9rem]"
          value={draft.value}
          disabled={disabled}
          onChange={(e) => onChange({ value: e.target.value })}
          onKeyDown={onKeyDown}
        >
          <option value="">—</option>
          {item.test.selectOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      );
    case 'culture':
      return (
        <div className="min-w-[18rem] space-y-1.5">
          <Input
            {...entryProps}
            className="h-8"
            placeholder={tr('lab', 'organism_or_no_growth')}
            value={draft.organism}
            disabled={disabled}
            onChange={(e) => onChange({ organism: e.target.value })}
            onKeyDown={onKeyDown}
          />
          <Input
            className="h-8 text-xs"
            placeholder={tr('lab', 'colony_count_e_g_10_5_cfu_ml')}
            value={draft.colonyCount}
            disabled={disabled}
            onChange={(e) => onChange({ colonyCount: e.target.value })}
          />
          {draft.sensitivities.map((s, index) => (
            <div key={index} className="flex gap-1">
              <Input
                className="h-7 text-xs"
                placeholder={tr('lab', 'antibiotic')}
                value={s.antibiotic}
                disabled={disabled}
                onChange={(e) =>
                  onChange({ sensitivities: draft.sensitivities.map((row, i) => (i === index ? { ...row, antibiotic: e.target.value } : row)) })
                }
              />
              <ChoiceButtons<Sensitivity['result']>
                size="sm"
                inline
                className="shrink-0"
                aria-label={s.antibiotic.trim() || tr('lab', 'antibiotic')}
                value={s.result}
                disabled={disabled}
                onChange={(value) =>
                  onChange({
                    sensitivities: draft.sensitivities.map((row, i) =>
                      i === index ? { ...row, result: value } : row,
                    ),
                  })
                }
                options={[
                  { value: 'sensitive', label: 'S', hint: humanize('sensitive') },
                  { value: 'intermediate', label: 'I', hint: humanize('intermediate') },
                  { value: 'resistant', label: 'R', hint: humanize('resistant') },
                ]}
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={tr('lab', 'remove_antibiotic')}
                disabled={disabled}
                onClick={() => onChange({ sensitivities: draft.sensitivities.filter((_, i) => i !== index) })}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          {!disabled && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => onChange({ sensitivities: [...draft.sensitivities, { antibiotic: '', result: 'sensitive' }] })}
            >
              <Plus className="me-1 h-3 w-3" /> {tr('lab', 'antibiotic')}
            </Button>
          )}
        </div>
      );
    case 'text':
      return (
        <div className="min-w-[14rem] space-y-1">
          <Input
            {...entryProps}
            className="h-8 min-w-[14rem]"
            value={draft.value}
            disabled={disabled}
            onChange={(e) => onChange({ value: e.target.value })}
            onKeyDown={onKeyDown}
          />
          <QuickValues item={item} disabled={disabled} onPick={(value) => onChange({ value })} />
        </div>
      );
    default:
      return (
        <div className="space-y-1">
          <Input
            {...entryProps}
            className="h-8 w-28 text-end tabular"
            inputMode="decimal"
            step={item.test.decimals !== null && item.test.decimals !== undefined ? 1 / 10 ** item.test.decimals : undefined}
            value={draft.value}
            disabled={disabled}
            onChange={(e) => onChange({ value: e.target.value })}
            onBlur={(e) => {
              const digits = item.test.decimals;
              const parsed = parseNumeric(e.target.value);
              if (digits !== null && digits !== undefined && parsed !== null && /^-?\d+(?:\.\d+)?$/.test(e.target.value.trim())) {
                onChange({ value: parsed.toFixed(digits) });
              }
            }}
            onKeyDown={onKeyDown}
          />
          <QuickValues item={item} disabled={disabled} onPick={(value) => onChange({ value })} />
        </div>
      );
  }
}

function QuickValues({ item, disabled, onPick }: { item: SheetItem; disabled: boolean; onPick: (value: string) => void }) {
  if (!item.test.quickValues || item.test.quickValues.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {item.test.quickValues.map((value) => (
        <button
          key={value}
          type="button"
          disabled={disabled}
          onClick={() => onPick(value)}
          className="rounded border px-1.5 py-0.5 text-[11px] hover:bg-accent disabled:opacity-50"
        >
          {value}
        </button>
      ))}
    </div>
  );
}

export function analyserChange(item: SheetItem, draft: Draft | undefined, mode: AnalyserOverrideMode | undefined): 'none' | 'free' | 'reason' | 'approval' | 'verifier' {
  const analyserValue = item.latestResult?.analyserValue;
  if (analyserValue == null || !draft || item.test.resultType === 'culture') return 'none';
  if (draft.value.trim() === '' || draft.value.trim() === analyserValue) return 'none';
  if (!mode || mode === 'technician') return 'free';
  if (mode === 'verifier_only') return 'verifier';
  return mode === 'with_approval' ? 'approval' : 'reason';
}
